import { useCallback, useRef, useState, type ReactNode } from 'react'
import { usePhoneSession } from '@/auth/pin/phoneSession'
import { hasAnyRole, ROLES } from '@/auth/roles'
import type { TeamMember } from '@/offline'
import { SupervisorPinSheet } from './SupervisorPinSheet'

/**
 * "The supervisor enters his own PIN on her phone" (F4.1 roll assignment, F4.4 groundnut skip,
 * F4.7 underweight sample, F4.6-h re-tag). The PIN is checked on the phone against the roster's
 * PIN hash, with the same lockout as unlock (R11, N-F4-03). Resolves with the supervisor, or null.
 * When the unlocked actor is already a supervisor, no PIN is asked.
 */
export function useSupervisorPin(): {
  request: (reason: ReactNode) => Promise<TeamMember | null>
  sheet: ReactNode
} {
  const { actor } = usePhoneSession()
  const [reason, setReason] = useState<ReactNode | null>(null)
  const resolver = useRef<((m: TeamMember | null) => void) | null>(null)

  const request = useCallback(
    (why: ReactNode) => {
      if (actor && hasAnyRole(actor.roles, [ROLES.SUPERVISOR])) return Promise.resolve(actor)
      return new Promise<TeamMember | null>((resolve) => {
        resolver.current = resolve
        setReason(why)
      })
    },
    [actor],
  )

  const finish = useCallback((m: TeamMember | null) => {
    resolver.current?.(m)
    resolver.current = null
    setReason(null)
  }, [])

  const sheet = reason !== null ? <SupervisorPinSheet reason={reason} onDone={finish} /> : null
  return { request, sheet }
}
